import { memo, useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Link, TableCell } from '@mui/material';
import styles from './CompanyRoleList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { companyRoleActions } from '~/thunks/company-role/companyRoleSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { ICompanyRole } from '~/utils/interface/company-role';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { MenuVerticalIcon } from '~/assets/icons';
import MenuOption from '../menu-option/MenuOption';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type CompanyRoleListProps = {
  onOpenEdit?: (id: string) => void;
  editData?: ICompanyRole;
  isHideMenu?: boolean;
};

function CompanyRoleList(props: CompanyRoleListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/company-role']);
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { companyRoles, filter, currentPage, totalPages } = useAppSelector((state) => state.companyRole);
  //#endregion Selector

  //#region Declare State
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('property.name'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'createdAt',
        label: t('property.createdAt'),
        sortable: true,
        searchable: true,
      },
    ],
    [t]
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(companyRoleActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleChangePage = (newPage: number) => {
    dispatch(
      companyRoleActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      companyRoleActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, selectedId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(selectedId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined && props.onOpenEdit) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(companyRoleActions.changeMenuSelectId(menuSelectedId));
      dispatch(companyRoleActions.changeOpenAlertDelete(true));
    }
  };

  //#endregion Handle Function
  return (
    <Box className={cx('companyRole-list-container')}>
      <BaseTable<ICompanyRole>
        data={companyRoles}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(companyRole: ICompanyRole) => {
          return (
            <>
              <TableCell>
                <Box>
                  <Link
                    onClick={() => {
                      setMenuSelectedId(companyRole.id);
                      if (menuSelectedId !== undefined && props.onOpenEdit) props.onOpenEdit(companyRole.id);
                    }}
                    className='underline text-[#007AFC]'
                  >
                    {companyRole?.name}
                  </Link>
                </Box>
              </TableCell>

              <TableCell>{dayjs(companyRole.createdAt).format('MMM DD, YYYY')}</TableCell>

              <TableCell align={'left'} padding={'none'}>
                {!props.isHideMenu &&
                  (permissions[PERMISSIONS.companyRole_edit] || permissions[PERMISSIONS.companyRole_delete]) && (
                    <Box className='flex flex-1 justify-center'>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenu(event, companyRole?.id);
                        }}
                      >
                        <MenuVerticalIcon className='w-[19px] h-[19px]' />
                      </IconButton>
                    </Box>
                  )}
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.companyRole_edit]}
        enableDelete={permissions[PERMISSIONS.companyRole_delete]}
      />
    </Box>
  );
}

export default memo(CompanyRoleList);
